import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@material-ui/core';
import { Control, useController } from 'react-hook-form';
import { ProtectedField } from '../TaxIdFieldGroupV2/ProtectedField';
import { DocumentNode } from 'graphql';
import { ranRegexValidation } from '@fluentsms/utils';
import ConfirmationDialog from '../ConfirmationDialog';
import { useTranslation } from '@fluentsms/i18n';
import { UnprotectedField } from '../UnprotectedField';

type GetFieldProps = {
  (
    /** Name prop to apply to the Textfield
     * can be shallow string or complex object using lodash's get syntax
     * example:
     *
     * `'firstName'`
     *
     * `'phoneNumbers[0].extension'`
     **/
    name: string,
  ): {
    /**
     * name prop
     */
    name: string;
    /** error prop **/
    error: boolean;
    /** helperText prop **/
    helperText: string;
  };
};

export interface ErrorState {
  hasError: boolean;
  message: string;
}

export interface ObscurableFieldValue {
  protectedValue: string;
  protectedValueMasked?: string;
}

export interface ObscurableFieldV2Props {
  getFieldProps: GetFieldProps;
  valueFormName: string;
  control?: Control<Record<string, any>>;
  hasFieldPermission?: boolean;
  getProtectedIdQuery?: DocumentNode;
  getProtectedIdVariables?: object;
  protectedIdQueryKey?: string;
  disabled?: boolean;
  validationRegex?: RegExp;
  errorMessage?: string;
  isRAN?: boolean;
  label?: string;
  mask?: string;
  isExplicitSave?: boolean;
  dataTestId?: string;
}

const usePrevious = (value: string | any) => {
  const ref = useRef();
  useEffect(() => {
    if (value?.length) {
      ref.current = value;
    }
  });
  return ref.current;
};

export const ObscurableFieldV2 = (props: ObscurableFieldV2Props) => {
  const {
    getFieldProps,
    control,
    valueFormName,
    hasFieldPermission = false,
    getProtectedIdQuery,
    getProtectedIdVariables,
    protectedIdQueryKey,
    disabled,
    label,
    mask = '***********',
    validationRegex = ranRegexValidation,
    errorMessage = 'Number must be up to 11 digits or empty',
    isRAN = false,
    isExplicitSave = false,
    dataTestId,
  } = props;

  const [hidden, setHidden] = useState<boolean>(true);
  const [isVisible, setVisible] = useState<boolean>(false);
  const [showProtectedField, setShowProtectedField] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState>({ hasError: false, message: '' });
  const [explicitSaveEditedValue, setExplicitSaveEditedValue] = useState('');

  const { t } = useTranslation('orders');

  const maskChar = 'X';

  const {
    field: { ref: obscurableIdRef, ...obscurableNumberProps },
  } = useController({ name: valueFormName, control, defaultValue: '' });

  const calculateMask = (value: string): string => {
    const length = value.length;
    const substringStart = length - (length > 4 ? 4 : length);
    return `${maskChar.repeat(length > 4 ? length - 4 : 0)}${value?.substring(substringStart)}`;
  };

  const [fieldValue, setFieldValue] = useState<ObscurableFieldValue>({
    protectedValue: '',
    protectedValueMasked: '',
  });

  const taxIdValue = hidden
    ? fieldValue.protectedValueMasked || obscurableNumberProps.value
    : fieldValue.protectedValue;
  const fallbackValue = usePrevious(
    !taxIdValue?.includes('X') || taxIdValue === fieldValue.protectedValue ? taxIdValue : taxIdValue,
  );

  const handleChange = (data: Partial<ObscurableFieldValue>) => {
    const newState = { ...fieldValue, ...data };
    newState.protectedValueMasked = calculateMask(newState.protectedValue);
    setFieldValue(newState);

    let errorState = { hasError: false, message: '' };

    if (!data?.protectedValue?.replace(/[-_]/g, '').match(validationRegex) && data.protectedValue) {
      if (!isRAN) {
        errorState = { hasError: true, message: errorMessage };
      }
      setIsEditing(true);
      setVisible(true);
    }

    setError(errorState);

    if (!newState.protectedValue.includes(maskChar) && !newState.protectedValue.includes('_') && !errorState.hasError) {
      obscurableNumberProps.onChange(newState.protectedValue);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    const value = event.target?.value;
    setExplicitSaveEditedValue(value);

    if (value?.includes(maskChar)) {
      event.preventDefault();
      setShowConfirmation(true);
    } else {
      if (hidden) {
        handleChange({ protectedValue: value.substring(value.length - 1) });
        setHidden(false);
      }
      handleChange({ protectedValue: value.replace(/[-_]/g, '') });
    }
  };

  const handleBlur = () => {
    setHidden(true);
    setVisible(false);
    setIsEditing(false);
    setShowProtectedField(false);
    setIsFocused(false);
  };

  const eyeballHandler = () => {
    if (!isVisible) {
      setShowProtectedField(true);
      setVisible(true);
      setHidden(false);
      setIsFocused(true);
    } else {
      handleBlur();
    }
  };

  const IdFieldProps = {
    ...getFieldProps(valueFormName),
    onBlur: obscurableNumberProps.onBlur,
    'data-testid': 'taxId-number',
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={12}>
          {showProtectedField ? (
            <ProtectedField
              value={taxIdValue}
              fallbackValue={fallbackValue}
              taxIdKey={protectedIdQueryKey}
              isEditing={isEditing}
              variables={getProtectedIdVariables}
              query={getProtectedIdQuery}
              onBlur={handleBlur}
              onChange={onChange}
              isVisible={isVisible}
              hasTaxIdPermission={hasFieldPermission}
              error={error}
              label={label}
              mask={mask}
              regex={validationRegex}
              isExplicitSave={isExplicitSave}
              explicitSaveEditedValue={explicitSaveEditedValue}
              dataTestId={dataTestId}
            />
          ) : (
            <UnprotectedField
              disabled={disabled || !hasFieldPermission}
              textFieldProps={IdFieldProps}
              isVisible={isVisible}
              error={error.hasError}
              helperText={error.message}
              value={taxIdValue}
              onBlur={handleBlur}
              onChange={onChange}
              eyeballHandler={eyeballHandler}
              label={label ?? ''}
              mask={mask}
              endAdornment={hasFieldPermission}
              dataTestId={dataTestId}
            />
          )}
        </Grid>
      </Grid>
      <ConfirmationDialog
        open={showConfirmation}
        autoFocus={false}
        dialogTitle={t('buyerSellerConfirmationText.editMaskedField')}
        dialogText={t('buyerSellerConfirmationText.editMaskedFieldDialogText')}
        primaryActionLabel={t('buyerSellerConfirmationText.continue')}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => {
          setFieldValue({ protectedValue: '', protectedValueMasked: '' });
          setShowConfirmation(false);
          setHidden(false);
          setIsFocused(true);
        }}
      />
    </>
  );
};
